import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';

const StoresReports = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [stats, setStats] = useState({
    inventoryValue: {},
    lowStockCount: 0,
    pendingOrders: {},
    monthlyConsumption: {},
    categoryStock: [],
    topConsumed: [],
    ordersByStatus: []
  });

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444'
  };

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const data = await apiGet('/admin/stores/reports/stats');
      if (data.success) setStats(data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
    setLoading(false);
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    pageTitle: {
      fontSize: isMobile ? '20px' : isTablet ? '24px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
      gap: isMobile ? '12px' : '20px',
      marginBottom: isMobile ? '20px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    statCard: {
      background: colors.white,
      padding: isMobile ? '20px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 4px 12px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`
    },
    statValue: {
      fontSize: isMobile ? '24px' : '32px',
      fontWeight: '800',
      marginBottom: '8px'
    },
    statLabel: {
      fontSize: isMobile ? '11px' : '12px',
      color: colors.textLight,
      textTransform: 'uppercase',
      fontWeight: '600'
    },
    contentCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px 16px 4px' : '0 0 24px 0'
    },
    sectionTitle: {
      fontSize: isMobile ? '16px' : '20px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: isMobile ? '12px' : '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: isMobile ? '12px' : '13px'
    },
    tableHeader: {
      background: colors.lightGray,
      fontWeight: '700',
      textAlign: 'left',
      padding: isMobile ? '10px' : '12px',
      borderBottom: `2px solid ${colors.mediumGray}`
    },
    tableCell: {
      padding: isMobile ? '10px' : '12px',
      borderBottom: `1px solid ${colors.lightGray}`
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading reports...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Stores Reports</h1>
        <p style={{ fontSize: isMobile ? '12px' : '14px', color: colors.textLight }}>
          {isMobile ? 'Stock analytics' : 'Inventory reports, stock analytics, and procurement insights'}
        </p>
      </div>

      {/* Statistics Cards */}
      <div style={styles.statsGrid}>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.primary}` }}>
          <div style={{ ...styles.statValue, color: colors.primary }}>
            {formatCurrency(stats.inventoryValue.total_value)}
          </div>
          <div style={styles.statLabel}>Total Inventory Value</div>
          <div style={{ fontSize: '11px', color: colors.textLight, marginTop: '4px' }}>
            {stats.inventoryValue.total_items} items
          </div>
        </div>

        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.danger}` }}>
          <div style={{ ...styles.statValue, color: colors.danger }}>
            {stats.lowStockCount}
          </div>
          <div style={styles.statLabel}>Low Stock Items</div>
        </div>

        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.warning}` }}>
          <div style={{ ...styles.statValue, color: colors.warning }}>
            {stats.pendingOrders.count || 0}
          </div>
          <div style={styles.statLabel}>Pending Orders</div>
          <div style={{ fontSize: '11px', color: colors.textLight, marginTop: '4px' }}>
            {formatCurrency(stats.pendingOrders.value)}
          </div>
        </div>

        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.accent}` }}>
          <div style={{ ...styles.statValue, color: colors.accent }}>
            {formatCurrency(stats.monthlyConsumption.consumption_value)}
          </div>
          <div style={styles.statLabel}>This Month Consumption</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: isMobile ? '16px' : '20px' }}>
        {/* Stock by Category */}
        <div style={styles.contentCard}>
          <h2 style={styles.sectionTitle}><span>📊</span> Stock Value by Category</h2>
          {stats.categoryStock.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
              No data available
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {stats.categoryStock.map((cat, index) => {
                const maxValue = Math.max(...stats.categoryStock.map(c => parseFloat(c.category_value)));
                const percentage = (parseFloat(cat.category_value) / maxValue) * 100;

                return (
                  <div key={index}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px' }}>
                      <span style={{ fontWeight: '600', textTransform: 'capitalize' }}>
                        {cat.category}
                      </span>
                      <span style={{ color: colors.textLight }}>
                        {cat.items_count} items
                      </span>
                    </div>
                    <div style={{
                      height: '36px',
                      background: colors.primary,
                      borderRadius: '6px',
                      width: `${percentage}%`,
                      minWidth: '100px',
                      display: 'flex',
                      alignItems: 'center',
                      paddingLeft: '12px',
                      color: colors.white,
                      fontSize: '13px',
                      fontWeight: '700'
                    }}>
                      {formatCurrency(cat.category_value)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top Consumed Items */}
        <div style={styles.contentCard}>
          <h2 style={styles.sectionTitle}><span>🔥</span> Top Consumed Items (This Month)</h2>
          {stats.topConsumed.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
              No consumption data
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {stats.topConsumed.map((item, index) => (
                <div
                  key={index}
                  style={{
                    padding: isMobile ? '12px' : '14px',
                    background: colors.lightGray,
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <div style={{ fontSize: isMobile ? '14px' : '15px', fontWeight: '600', color: colors.textDark }}>
                      {item.item_name}
                    </div>
                    <div style={{ fontSize: '11px', color: colors.textLight }}>
                      {parseFloat(item.total_consumed).toFixed(2)} {item.unit} consumed
                    </div>
                  </div>
                  <div style={{ fontSize: isMobile ? '15px' : '16px', fontWeight: '700', color: colors.primary }}>
                    {formatCurrency(item.consumption_value)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Purchase Orders by Status */}
      <div style={styles.contentCard}>
        <h2 style={styles.sectionTitle}><span>📋</span> Purchase Orders by Status</h2>
        {stats.ordersByStatus.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
            No purchase orders
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>Status</th>
                  <th style={styles.tableHeader}>Count</th>
                  <th style={styles.tableHeader}>Total Value</th>
                </tr>
              </thead>
              <tbody>
                {stats.ordersByStatus.map((status, index) => (
                  <tr key={index}>
                    <td style={styles.tableCell}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background: status.status === 'received' ? `${colors.success}20` :
                          status.status === 'approved' ? `${colors.accent}20` :
                            status.status === 'pending' ? `${colors.warning}20` : `${colors.danger}20`,
                        color: status.status === 'received' ? colors.success :
                          status.status === 'approved' ? colors.accent :
                            status.status === 'pending' ? colors.warning : colors.danger,
                        textTransform: 'capitalize'
                      }}>
                        {status.status}
                      </span>
                    </td>
                    <td style={styles.tableCell}><strong>{status.count}</strong></td>
                    <td style={styles.tableCell}>
                      <strong>{formatCurrency(status.total_value)}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default StoresReports;